<template>
  <!-- Desktop: persistent shell with inner content transitions -->
  <div v-if="isDesktop" class="pds-layout">
    <header class="pds-topbar">
      <h1 class="pds-topbar-title">{{ $t('profile.headline') }}</h1>
    </header>

    <aside class="pds-sidebar">
      <ProfileDesktopSidebar />
    </aside>

    <div class="pds-content">
      <div class="pds-content-inner">
        <router-view v-slot="{ Component }">
          <Transition name="pds-fade" mode="out-in">
            <component :is="Component" :key="route.path" />
          </Transition>
        </router-view>
      </div>
    </div>
  </div>

  <!-- Mobile: transparent pass-through (App.vue handles transitions) -->
  <router-view v-else />
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { useIsDesktop } from '../composables/useIsDesktop'
import ProfileDesktopSidebar from '../components/desktop/ProfileDesktopSidebar.vue'

const isDesktop = useIsDesktop()
const route = useRoute()
</script>

<style scoped>
.pds-layout {
  --pds-topbar-h: 4rem;
  display: flex;
  flex-wrap: wrap;
  min-height: 100vh;
}

/* ── Top bar ── */
.pds-topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--pds-topbar-h);
  display: flex;
  align-items: center;
  padding: 0 28px;
  background: rgba(255, 255, 255, 0.80);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  z-index: 30;
}

:is(.dark *).pds-topbar {
  background: rgba(30, 32, 36, 0.80);
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.pds-topbar-title {
  font-family: 'Lato', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: var(--foreground);
  letter-spacing: -0.01em;
}

/* ── Sidebar ── */
.pds-sidebar {
  position: fixed;
  left: 0;
  top: var(--pds-topbar-h);
  height: calc(100vh - var(--pds-topbar-h));
  width: var(--sidebar-width);
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 20;
  overflow: hidden;
}

:is(.dark *).pds-sidebar {
  background: rgba(255, 255, 255, 0.03);
  border-right-color: rgba(255, 255, 255, 0.06);
}

/* ── Content area ── */
.pds-content {
  flex: 1;
  padding-left: calc(var(--sidebar-width) + var(--sidebar-gap));
  padding-top: var(--pds-topbar-h);
  min-height: 100vh;
}

.pds-content-inner {
  max-width: 720px;
  padding: 8px 28px 48px;
}

/* ── Content crossfade transition ── */
.pds-fade-enter-active {
  transition: opacity 0.15s ease;
}
.pds-fade-leave-active {
  transition: opacity 0.1s ease;
}
.pds-fade-enter-from,
.pds-fade-leave-to {
  opacity: 0;
}
</style>
